import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

// ── types (mirror link-backend/src/tasks) ───────────────────────────────────
export interface TaskPerson {
  userId: string;
  userName: string;
  name: string;
  department: string;
  profileImg: string;
}
export interface TaskFile {
  name: string;
  url: string;
}
export interface TaskReply {
  id: string;
  kind: 'created' | 'reply' | 'done' | 'accept' | 'reopen' | 'waiting' | 'waiting_cleared' | 'assign' | 'pickup' | 'due' | 'edit' | 'cancel' | 'escalated' | 'auto_accept' | string;
  by: TaskPerson;
  at: string;
  text: string;
  files: TaskFile[];
  meta?: Record<string, any>;
}
export interface TaskLink {
  type: 'project' | 'lead' | 'vendor_request' | 'invoice' | 'chat' | 'hire' | 'other' | string;
  id?: string;
  label?: string;
  url?: string;
}
// ── task types and proof of work (mirror link-backend/src/tasks/task-types.ts) ──
export type ProofKind = 'none' | 'photo' | 'photos' | 'document' | 'sheet' | 'video' | 'any';
export interface TaskTypeDef {
  id: string;
  label: string;
  group: string;
  icon: string;
  proof: ProofKind;
  keywords: string[];
  depts: string[];
}
export interface TaskTypeGroup {
  id: string;
  label: string;
}
export interface ProofKindDef {
  id: ProofKind;
  label: string;
  short: string;
  icon: string;
}
export interface TaskTypes {
  groups: TaskTypeGroup[];
  types: TaskTypeDef[];
  proofKinds: ProofKindDef[];
}
/** Proof a task asks for before it can be marked done, as the server presents it. */
export interface TaskProof {
  /** Every kind the task asks for; `kind` is the first, kept for older rows. */
  kinds: ProofKind[];
  kind: ProofKind;
  note: string;
  label: string;
  short: string;
  icon: string;
}
export type RepeatFreq = 'daily' | 'weekly' | 'monthly' | 'quarterly';
export interface RepeatRule {
  freq: RepeatFreq;
  days?: number[];
  dayOfMonth?: number;
  label?: string;
}
export interface TaskSeriesSummary {
  id: string;
  title: string;
  status: 'active' | 'paused' | 'ended';
  rule: RepeatRule;
  label: string;
  startsOn: string;
  endsOn: string | null;
  nextDue: string | null;
  occurrences: number;
  canManage: boolean;
}
export interface TaskCan {
  edit: boolean;
  due: boolean;
  reply: boolean;
  done: boolean;
  accept: boolean;
  reopen: boolean;
  waiting: boolean;
  assign: boolean;
  pickup: boolean;
  cancel: boolean;
  delete: boolean;
  subtask: boolean;
  /** Send a nudge to the owner (requester, their head, an Admin); never to yourself. */
  remind: boolean;
  /** Ask the requester for a later due date (the owner, with a reason). */
  extend: boolean;
  /** Approve or decline an open request for more time (requester, their head, the owner's manager, an Admin). */
  decideExtension: boolean;
  /** Pause the clock with a reason (requester or Admin); resume moves the due date by the days held. */
  hold: boolean;
  resume: boolean;
}
export type TaskStatus = 'open' | 'done' | 'closed' | 'cancelled';
export type DueState = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none';
/** One extension asked by the owner or given by the requester; kept on the task for good. */
export interface TaskExtension {
  id: string;
  kind: 'asked' | 'given';
  status: 'pending' | 'approved' | 'declined';
  fromDue: string | null;
  toDue: string;
  reason: string;
  by: TaskPerson;
  at: string;
  decidedBy: TaskPerson | null;
  decidedAt: string | null;
  note: string;
}
export interface Task {
  id: string;
  title: string;
  details: string;
  status: TaskStatus;
  priority: 'normal' | 'high';
  /** Kind of work (catalogue id) and its display bits. */
  type: string;
  typeLabel: string;
  typeGroup: string;
  typeGroupLabel: string;
  typeIcon: string;
  proof: TaskProof | null;
  /** Optional time of day on the due date (HH:mm) and "4:00 pm". */
  dueTime: string | null;
  dueTimeLabel: string;
  remindedAt: string | null;
  /** Reminders people sent on this task (accountability count), and the last one. */
  reminderCount: number;
  lastRemindedAt: string | null;
  lastRemindedBy: TaskPerson | null;
  /** Where this task's record lives in chat (one-to-one, or the group task's own group). */
  chat: { channelId: string; type: 'single' | 'group'; url: string } | null;
  /** Times the due date was pushed later. */
  rescheduleCount: number;
  /** A group task: one child per person; the parent closes itself when all are done. */
  isGroup: boolean;
  groupSize: number;
  groupTargets: { userId?: string; name?: string; department?: string }[] | null;
  createdBy: TaskPerson;
  /** Delegate who created it on the requester's behalf. */
  via: TaskPerson | null;
  assignee: TaskPerson | null;
  department: string | null;
  dueDate: string | null;
  /** Set when an Admin back-dated the task to the day its work started. */
  startedOn?: string | null;
  link: TaskLink | null;
  source: string;
  parentTaskId: string | null;
  seriesId: string | null;
  occurrenceDue: string | null;
  repeat: RepeatRule | null;
  waitingOn: { user: TaskPerson; note: string; since: string } | null;
  waitingDays: number;
  /** Paused by the requester; the days on hold move the due date on resume. */
  hold: { since: string; by: TaskPerson; reason: string } | null;
  heldDays: number;
  holds: { since: string; until: string; by: TaskPerson; reason: string; resumedBy: TaskPerson; days: number }[];
  /** The owner's open request for more time. */
  extensionRequest: { id: string; toDue: string; reason: string; by: TaskPerson; at: string } | null;
  extensions: TaskExtension[];
  replies: TaskReply[];
  replyCount: number;
  isPersonal: boolean;
  doneAt: string | null;
  doneBy: TaskPerson | null;
  acceptedAt: string | null;
  acceptedBy: TaskPerson | null;
  reopenCount: number;
  cancelledAt: string | null;
  cancelReason: string;
  escalatedAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  age: number;
  overdueDays: number;
  dueState: DueState;
  dueWords: string;
  children: { total: number; closed: number } | Task[];
  mine: boolean;
  askedByMe: boolean;
  waitingOnMe: boolean;
  awaitingMyCheck: boolean;
  can: TaskCan;
}
/** One file from the task's thread (or from a group task's copies): the task's own gallery. */
export interface TaskGalleryFile {
  name: string;
  url: string;
  by: TaskPerson;
  at: string;
  kind: string;
  proof: boolean;
  taskId: string;
  taskTitle: string;
}
/** One of the latest thread lines from a sub-task (any depth), shown on the parent. */
export interface SubActivityLine {
  at: string;
  kind: string;
  text: string;
  by: TaskPerson;
  files: number;
  taskId: string;
  taskTitle: string;
  owner: TaskPerson | null;
  status: TaskStatus;
}
export interface TaskFull extends Omit<Task, 'children'> {
  children: Task[];
  childStats: { total: number; closed: number };
  parent: { id: string; title: string; status: TaskStatus } | null;
  series: TaskSeriesSummary | null;
  /** Files from the task and everything under it, any depth. */
  files: TaskGalleryFile[];
  subActivity: SubActivityLine[];
}
export interface TaskCounts {
  today: number;
  overdue: number;
  mine: number;
  asked: number;
  team: number;
  department: number;
  done: number;
  waitingOnMe: number;
  toCheck: number;
}
export interface TaskMe {
  userId: string;
  name: string;
  department: string;
  admin: boolean;
  viewAll: boolean;
  actsFor: TaskPerson[];
  headOf: string[];
  hasTeam: boolean;
}
export type TaskView = 'today' | 'mine' | 'asked' | 'team' | 'department' | 'done';
/** Page views: the list views plus the people table (heads and directors). */
export type PageView = TaskView | 'people';
export interface PersonStats {
  person: TaskPerson;
  open: number;
  overdue: number;
  high: number;
  dueToday: number;
  toCheck: number;
  closed30: number;
  judged: number;
  onTime: number;
  onTimeRate: number | null;
  avgLateDays: number | null;
  reopened: number;
  escalated30: number;
  personalOpen: number;
  /** Reminders sent on this person's open tasks, and how many of those tasks got one. */
  reminders: number;
  remindedOpen: number;
  waitingOnThem: number;
  oldest: { id: string; title: string; age: number; dueDate: string | null; overdueDays: number } | null;
}
/** My on-time score (GET /tasks/my-score): judged = finished tasks that had a due date. */
export interface TaskScore {
  today: string;
  month: { done: number; onTime: number; late: number; pct: number | null; extended: number };
  all: { done: number; onTime: number; late: number; pct: number | null; extended: number };
  streak: number;
  bestStreak: number;
  finishedWithoutDue: number;
}
export interface PeopleStats {
  /** The period done / on-time / lateness were counted in, and the custom list if one was asked for. */
  window?: { from: string; to: string; days: number };
  picked?: string[] | null;
  today: string;
  scope: 'everyone' | 'team';
  people: PersonStats[];
  totals: { open: number; overdue: number; toCheck: number; closed30: number };
}
export interface TaskListResult {
  view: TaskView;
  today: string;
  items: Task[];
  counts: TaskCounts;
  me: TaskMe;
}
export interface TaskPersonOption extends TaskPerson {
  isHead: boolean;
}
export interface TaskDepartment {
  name: string;
  members: number;
  heads: string[];
}
export interface TaskBadge {
  today: number;
  overdue: number;
  toCheck: number;
  waitingOnMe: number;
}
export interface NewTaskBody {
  title: string;
  details?: string;
  assigneeId?: string;
  department?: string;
  dueDate?: string | null;
  /** HH:mm on the due date; the owner gets a reminder then. */
  dueTime?: string | null;
  priority?: 'normal' | 'high';
  /** Catalogue id; the server tags from the title when missing. */
  type?: string;
  /** Proof needed when done: one kind, several kinds, or nothing. */
  proof?: ProofKind | ProofKind[] | { kinds: ProofKind[]; note?: string } | null;
  /** Several people and/or departments: one task each (everyone=true gives one task per member). */
  targets?: { userId?: string; department?: string; everyone?: boolean }[];
  link?: TaskLink | null;
  files?: TaskFile[];
  source?: string;
  sourceRef?: Record<string, any>;
  parentTaskId?: string;
  /** Admin userId when creating the task in their name (needs tasks.assign_on_behalf). */
  onBehalfOf?: string;
  /** Admins only, until TASK_BACKDATE_ALLOWED_UNTIL: the day the work started; the task then counts from that day. */
  startedOn?: string | null;
  /** Repeating task: the first due date is dueDate; the rule makes the rest. */
  repeat?: { freq: RepeatFreq; days?: number[]; dayOfMonth?: number; endsOn?: string | null };
}
/** The type whose longest keyword appears in the title; 'general' when none does (same rule as the server). */
export const detectTaskType = (title: string, types: TaskTypeDef[]): string => {
  const s = ` ${String(title || '').toLowerCase().replace(/\s+/g, ' ')} `;
  let best: { id: string; len: number } | null = null;
  for (const t of types) {
    for (const k of t.keywords) {
      let i = s.indexOf(k);
      while (i >= 0 && /[a-z0-9]/.test(s[i - 1] || ' ')) i = s.indexOf(k, i + 1); // must start a word
      if (i >= 0 && (!best || k.length > best.len)) best = { id: t.id, len: k.length };
    }
  }
  return best?.id || 'general';
};
const IMAGE_EXT = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.heic', '.heif'];
const SHEET_EXT = ['.xls', '.xlsx', '.csv'];
const VIDEO_EXT = ['.mp4', '.mov', '.webm', '.m4v'];
const DOC_EXT = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.ppt', '.pptx', '.txt'];
export const taskFileExt = (f: { name?: string; url?: string }): string => {
  const n = String(f?.name || f?.url || '').toLowerCase().split('?')[0];
  const m = n.match(/\.[a-z0-9]+$/);
  return m ? m[0] : '';
};
/** Same rule as the server: do these files satisfy the proof asked for? */
export const proofSatisfied = (kind: ProofKind | null | undefined, files: { name?: string; url?: string }[]): boolean => {
  if (!kind || kind === 'none') return true;
  const exts = (files || []).map(taskFileExt);
  const count = (list: string[]) => exts.filter((e) => list.includes(e)).length;
  if (kind === 'photo') return count(IMAGE_EXT) >= 1;
  if (kind === 'photos') return count(IMAGE_EXT) >= 2;
  if (kind === 'document') return count(DOC_EXT) >= 1;
  if (kind === 'sheet') return count(SHEET_EXT) >= 1;
  if (kind === 'video') return count(VIDEO_EXT) >= 1;
  return exts.length >= 1;
};
/** The kinds a task's proof asks for (old and new shapes). */
export const proofKindsOf = (p: { kinds?: ProofKind[]; kind?: ProofKind } | null | undefined): ProofKind[] => (p?.kinds?.length ? p.kinds : p?.kind && p.kind !== 'none' ? [p.kind] : []);
/** The kinds these files do not satisfy yet. */
export const proofMissing = (kinds: ProofKind[], files: { name?: string; url?: string }[]): ProofKind[] => kinds.filter((k) => !proofSatisfied(k, files));
/** What the attach button should accept for a proof kind. */
export const proofAccept = (kind: ProofKind | null | undefined): string => {
  if (kind === 'photo' || kind === 'photos') return 'image/*,.heic,.heif';
  if (kind === 'sheet') return '.xls,.xlsx,.csv';
  if (kind === 'video') return 'video/*,.mp4,.mov,.webm,.m4v';
  if (kind === 'document') return '.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt';
  return '.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.svg,.gif,.heic,.heif,.txt,.mp4,.mov,.webm,.m4v';
};
/** "4:00 pm" from "16:00". */
export const taskTimeLabel = (hhmm: string | null | undefined): string => {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return '';
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
};
const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** "weekly · Mon, Wed" for chips. */
export const repeatShort = (r: RepeatRule | null | undefined): string => {
  if (!r) return '';
  if (r.freq === 'weekly') return r.days?.length ? `weekly · ${r.days.map((d) => DOW_SHORT[d]).join(', ')}` : 'weekly';
  if (r.freq === 'monthly') return `monthly · ${r.dayOfMonth || 1}`;
  if (r.freq === 'quarterly') return `quarterly · ${r.dayOfMonth || 1}`;
  return 'daily';
};

// ── date helpers (the browser's own calendar day) ───────────────────────────
const pad = (n: number) => String(n).padStart(2, '0');
export const taskTodayYMD = (offset = 0): string => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
/** Mirrors link-backend src/tasks/constants.ts: Admins may back-date a new task's start until this day (inclusive) — a one-week provision from 20 Oct 2026. */
export const TASK_BACKDATE_ALLOWED_UNTIL = '2026-10-27';
export const TASK_BACKDATE_MAX_DAYS = 180;
export const canBackdateTasks = (department = localStorage.getItem('department') || '', today = taskTodayYMD()): boolean =>
  department.toUpperCase() === 'ADMIN' && today <= TASK_BACKDATE_ALLOWED_UNTIL;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** "Fri 17 Oct", or "Today" / "Tomorrow" / "Yesterday". */
export const taskDueLabel = (ymd: string | null | undefined): string => {
  if (!ymd) return 'No date';
  const diff = daysFromToday(ymd);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const sameYear = dt.getFullYear() === new Date().getFullYear();
  return `${DAYS[dt.getDay()]} ${d} ${MONTHS[m - 1]}${sameYear ? '' : ' ' + y}`;
};
export const daysFromToday = (ymd: string): number => {
  const [y, m, d] = ymd.split('-').map(Number);
  const a = new Date(y, m - 1, d).getTime();
  const t = new Date();
  const b = new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
  return Math.round((a - b) / 86400000);
};
/** "2m ago", "3h ago", "Yesterday", "Fri 10 Oct" for the thread. */
export const taskWhen = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24 && new Date(iso).getDate() === new Date().getDate()) return `${h}h ago`;
  const d = new Date(iso);
  const ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const label = taskDueLabel(ymd);
  return `${label === 'Today' ? '' : label + ', '}${d.getHours() % 12 || 12}:${pad(d.getMinutes())} ${d.getHours() >= 12 ? 'pm' : 'am'}`;
};
export const taskInitials = (name: string | undefined | null): string =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('') || '?';

@Injectable({ providedIn: 'root' })
export class TasksService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.host}tasks`;

  /** Header badge counts, refreshed on load, on every change and every minute. */
  readonly badge = signal<TaskBadge | null>(null);
  /** Fires after any change made through this service (task id when known). Pages re-fetch on it. */
  readonly changed$ = new Subject<string | undefined>();

  private peopleCache: TaskPersonOption[] | null = null;
  private departmentsCache: TaskDepartment[] | null = null;
  private typesCache: TaskTypes | null = null;
  private typesPending: Promise<TaskTypes> | null = null;

  private unwrap<T>(res: any): T {
    return (res?.data ?? res) as T;
  }
  private async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    return this.unwrap<T>(await firstValueFrom(this.http.get(`${this.base}${path}`, { params })));
  }
  private async post<T>(path: string, body: any = {}): Promise<T> {
    const out = this.unwrap<T>(await firstValueFrom(this.http.post(`${this.base}${path}`, body)));
    return out;
  }
  private touched(id?: string) {
    this.changed$.next(id);
    void this.refreshBadge();
  }

  // ── reads ──────────────────────────────────────────────────────────────────
  list(view: TaskView, q = ''): Promise<TaskListResult> {
    return this.get<TaskListResult>('', { view, ...(q ? { q } : {}) });
  }
  getTask(id: string): Promise<TaskFull> {
    return this.get<TaskFull>(`/${id}`);
  }
  peopleStats(opts: { from?: string; to?: string; userIds?: string[] } = {}): Promise<PeopleStats> {
    const params: Record<string, string> = {};
    if (opts.from) params['from'] = opts.from;
    if (opts.to) params['to'] = opts.to;
    if (opts.userIds?.length) params['userIds'] = opts.userIds.join(',');
    return this.get<PeopleStats>('/people-stats', params);
  }
  async refreshBadge(): Promise<TaskBadge | null> {
    try {
      const b = await this.get<TaskBadge>('/badge');
      this.badge.set(b);
      return b;
    } catch {
      return null;
    }
  }
  async people(force = false): Promise<TaskPersonOption[]> {
    if (!force && this.peopleCache) return this.peopleCache;
    this.peopleCache = await this.get<TaskPersonOption[]>('/people');
    return this.peopleCache;
  }
  /** The type catalogue, fetched once per session. */
  types(): Promise<TaskTypes> {
    if (this.typesCache) return Promise.resolve(this.typesCache);
    if (!this.typesPending) {
      this.typesPending = this.get<TaskTypes>('/types')
        .then((t) => {
          this.typesCache = t;
          return t;
        })
        .finally(() => (this.typesPending = null));
    }
    return this.typesPending;
  }
  async departments(force = false): Promise<TaskDepartment[]> {
    if (!force && this.departmentsCache) return this.departmentsCache;
    this.departmentsCache = await this.get<TaskDepartment[]>('/departments');
    return this.departmentsCache;
  }

  // ── writes ─────────────────────────────────────────────────────────────────
  async create(body: NewTaskBody): Promise<TaskFull> {
    const t = await this.post<TaskFull>('', body);
    this.touched(t.id);
    return t;
  }
  async update(id: string, body: Partial<{ title: string; details: string; priority: 'normal' | 'high'; dueDate: string | null; dueTime: string | null; type: string; proof: ProofKind | ProofKind[] | { kinds: ProofKind[]; note?: string } | null; note: string }>): Promise<TaskFull> {
    const t = this.unwrap<TaskFull>(await firstValueFrom(this.http.put(`${this.base}/${id}`, body)));
    this.touched(id);
    return t;
  }
  async reply(id: string, text: string, files: TaskFile[] = []): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/replies`, { text, files });
    this.touched(id);
    return t;
  }
  async done(id: string, text = '', files: TaskFile[] = []): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/done`, { text, files });
    this.touched(id);
    return t;
  }
  /** Nudge the owner about an open task; the count shows on the task. */
  async remind(id: string, text = ''): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/remind`, { text });
    this.touched(id);
    return t;
  }
  async accept(id: string): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/accept`);
    this.touched(id);
    return t;
  }
  async reopen(id: string, text: string): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/reopen`, { text });
    this.touched(id);
    return t;
  }
  async waiting(id: string, userId: string, note = ''): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/waiting`, { userId, note });
    this.touched(id);
    return t;
  }
  async clearWaiting(id: string): Promise<TaskFull> {
    const t = this.unwrap<TaskFull>(await firstValueFrom(this.http.delete(`${this.base}/${id}/waiting`)));
    this.touched(id);
    return t;
  }
  /** The owner asks for a later due date, with a reason; the requester decides. */
  async askExtension(id: string, toDue: string, reason: string): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/extension`, { toDue, reason });
    this.touched(id);
    return t;
  }
  async decideExtension(id: string, approve: boolean, note = '', toDue?: string | null): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/extension/decide`, { approve, note, toDue: toDue || undefined });
    this.touched(id);
    return t;
  }
  /** Pause the clock with a reason; resume moves the due date by the days held. */
  async hold(id: string, reason: string): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/hold`, { reason });
    this.touched(id);
    return t;
  }
  async resume(id: string): Promise<TaskFull> {
    const t = this.unwrap<TaskFull>(await firstValueFrom(this.http.delete(`${this.base}/${id}/hold`)));
    this.touched(id);
    return t;
  }
  myScore(): Promise<TaskScore> {
    return this.get<TaskScore>('/my-score', {});
  }
  async assign(id: string, userId: string, note = ''): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/assign`, { userId, note });
    this.touched(id);
    return t;
  }
  async pickup(id: string): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/pickup`);
    this.touched(id);
    return t;
  }
  async cancel(id: string, reason: string): Promise<TaskFull> {
    const t = await this.post<TaskFull>(`/${id}/cancel`, { reason });
    this.touched(id);
    return t;
  }
  async remove(id: string): Promise<void> {
    await firstValueFrom(this.http.delete(`${this.base}/${id}`));
    this.touched(id);
  }
  async seriesAction(id: string, action: 'pause' | 'resume' | 'stop'): Promise<TaskSeriesSummary> {
    const s = await this.post<TaskSeriesSummary>(`/series/${id}/${action}`);
    this.touched();
    return s;
  }
  async upload(file: File): Promise<TaskFile> {
    const form = new FormData();
    form.append('file', file, file.name);
    const res: any = await firstValueFrom(this.http.post(`${this.base}/upload`, form));
    const d = res?.data ?? res;
    const url = d?.url || d?.data?.url;
    if (!url) throw new Error('upload failed');
    return { name: d?.name || file.name, url };
  }

  /** Plain-English text from an HTTP error, for the small red line under a form. */
  static errorText(err: any, fallback = 'Something went wrong'): string {
    const pick = (v: any): string | null => {
      if (!v) return null;
      if (typeof v === 'string') return v;
      if (Array.isArray(v)) return v.map(pick).filter(Boolean).join(', ') || null;
      if (typeof v === 'object') return pick(v.message) || pick(v.error) || null;
      return null;
    };
    return pick(err?.error?.message) || pick(err?.error) || pick(err?.message) || fallback;
  }
}
